import clsx from 'clsx';
import { times } from 'lodash-es';
import { Trans, useTranslation } from 'next-i18next/pages';
import dynamic from 'next/dynamic';
import { ReactNode } from 'react';

import { HomepageHeroSalaryHeadline, useMultipleVariantData } from '@pflegia/feature-flags';
import { useCookiesConsentManagerContext } from '@pflegia/ui/contexts/cookies-consent-manager.context';
import { useFlavorFromRoute } from '@pflegia/ui/hooks/use-flavor-from-route';
import { ReactComponent as ArrowVertical } from '@pflegia/ui/icons/arrow-vertical.svg';
import { ReactComponent as FreeAndAnonymousLogo } from '@pflegia/ui/icons/free-and-anonymous.svg';

import { JobSection } from '@domains/landing-page/related-jobs/RelatedJobs';

import SkeletonDivDefault from '@shared/components/skeleton/SkeletonDivDefault';

const RelatedJobs = dynamic(() => import('@domains/landing-page/related-jobs/RelatedJobs'), { ssr: false });
const JobDetailsProvider = dynamic(() => import('@shared/context/job-details.context').then(mod => mod.JobDetailsProvider), {
  ssr: false,
});

export interface GuestHeaderProps {
  heroButtons: ReactNode;
  headerImage: ReactNode;
  titleKey?: string;
  namespace?: string;
  subTitleKey?: string;
  showJobSearch?: boolean;
  jobSection?: JobSection;
  funnelLabel?: string;
}

const HERO_TEXT_LINES_COUNT = 4;

const GuestHeader: React.FC<GuestHeaderProps> = ({
  heroButtons,
  headerImage,
  titleKey = 'title',
  namespace = 'landingPage',
  subTitleKey,
  showJobSearch = false,
  jobSection,
  funnelLabel = 'funnelLabel',
}) => {
  const { t } = useTranslation(namespace, { keyPrefix: 'header' });
  const { payload: heroText } = useMultipleVariantData<HomepageHeroSalaryHeadline.PayloadType>(HomepageHeroSalaryHeadline.Name);
  const { isPraxia } = useFlavorFromRoute();
  const { ready: isConsentReady } = useCookiesConsentManagerContext();

  return (
    <>
      <div className='flex flex-col items-center overflow-hidden px-6 md:flex-row md:items-start md:justify-center md:gap-7 lg:gap-0'>
        {headerImage}
        <div className='flex flex-col items-center lg:ml-11 lg:items-start'>
          <h1
            className={clsx(
              'text-default mt-4 text-3xl lg:mt-16 lg:text-5xl lg:leading-normal xl:text-6xl xl:leading-normal',
              showJobSearch ? 'mb-0' : 'mb-6 md:mb-8',
            )}>
            {(isPraxia || namespace !== 'landingPage') && (
              <Trans components={{ span: <span className='text-primary' /> }} i18nKey={titleKey} t={t} />
            )}
            {namespace === 'landingPage' &&
              !isPraxia &&
              (isConsentReady && heroText ? (
                <Trans components={{ span: <span className='text-primary' /> }}>{heroText}</Trans>
              ) : (
                times(HERO_TEXT_LINES_COUNT).map(item => (
                  <SkeletonDivDefault key={`loading-${item}`} className='h-[calc(1.2em)] w-[12ch] animate-pulse rounded-sm bg-gray-200' />
                ))
              ))}
          </h1>
          {showJobSearch ? (
            <div className='relative h-10 min-h-max w-full md:h-20'>
              <ArrowVertical className='text-primary absolute right-1 h-8 w-3 -scale-x-100 md:bottom-auto md:h-16 md:w-7' />
            </div>
          ) : (
            <>
              {subTitleKey && <h2 className='mb-6 w-full text-base lg:-mt-5 lg:text-2xl'>{t(subTitleKey)}</h2>}
              <div className='mb-8 w-full'>
                <div className='relative mb-4 min-h-max lg:mb-7'>
                  <ArrowVertical className='text-primary absolute -bottom-5 -left-4 h-8 w-3 md:top-2 md:bottom-auto md:-left-8 md:h-16 md:w-7' />
                  <p className='text-secondary'>{t(funnelLabel)}</p>
                </div>
                {heroButtons}
              </div>
            </>
          )}
        </div>
      </div>
      {showJobSearch && (
        <JobDetailsProvider>
          <RelatedJobs jobSection={jobSection} />
        </JobDetailsProvider>
      )}
      <div className={clsx('mx-auto mt-6 flex w-fit flex-col gap-4 md:mx-0 md:ml-auto', isPraxia && 'mb-4 md:mb-8')}>
        <div className='align-center flex w-full gap-2'>
          <FreeAndAnonymousLogo />
          <span>{t('freeAndAnonymous')}</span>
        </div>
      </div>
    </>
  );
};

export default GuestHeader;
